// चतुर्थी = षष्ठी (नि.४६): a bracket drawn beside a declension grid that joins the two rows, and a short note
// that sets the two rows side by side. Both read the engine's forms; nothing is claimed that the table does not show.

import type { FC, ReactNode, RefObject } from 'react'
import type { Case, Num } from '@/prakrit/lib/grammar/types'
import type { GridCell } from '@/prakrit/components/grammar/grammar-grid'

import { useEffect, useState } from 'react'
import { Equal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NUMBERS } from '@/prakrit/lib/grammar/types'
import { CASE_LABEL, KARAK, NUMBER_LABEL } from '@/prakrit/lib/grammar/labels'
import { Bi } from '@/prakrit/components/common/bi'

type Rows = Partial<Record<Case, Record<Num, GridCell>>>

/** True when चतुर्थी and षष्ठी carry exactly the same core forms in both numbers. */
export const twinsMatch = (rows: Rows): boolean => {
    const a = rows.chaturthi
    const b = rows.shashthi

    if (!a || !b) return false

    return NUMBERS.every(n => a[n].core.join('|') === b[n].core.join('|') && a[n].core.length > 0)
}

interface TwinBracketProps {
    containerRef: RefObject<HTMLElement | null>
    /** data-row indexes of the चतुर्थी and षष्ठी rows in the grid, or null when either is hidden. */
    rows: [number, number] | null
    /** Changes whenever the grid's layout may change (noun, variant toggle, mode). */
    layoutKey: string
}

/** A bracket on the right of the grid from the चतुर्थी row to the षष्ठी row. The container needs pr-6 sm:pr-9 and position relative. */
export const TwinBracket: FC<TwinBracketProps> = ({ containerRef, rows, layoutKey }): ReactNode => {
    const [box, setBox] = useState<{ top: number, height: number } | null>(null)
    const a = rows ? rows[0] : -1
    const b = rows ? rows[1] : -1

    useEffect(() => {
        const el = containerRef.current

        if (!el || a < 0 || b < 0) {
            setBox(null)

            return
        }

        const measure = () => {
            const first = el.querySelector<HTMLElement>(`[data-row="${a}"][data-col="1"]`)
            const second = el.querySelector<HTMLElement>(`[data-row="${b}"][data-col="1"]`)

            if (!first || !second) {
                setBox(null)

                return
            }

            const base = el.getBoundingClientRect()
            const r1 = first.getBoundingClientRect()
            const r2 = second.getBoundingClientRect()
            const top = r1.top - base.top + r1.height / 2
            const bottom = r2.top - base.top + r2.height / 2

            setBox(bottom > top ? { top, height: bottom - top } : null)
        }

        measure()

        if (typeof ResizeObserver === 'undefined') return

        const observer = new ResizeObserver(measure)

        observer.observe(el)

        return () => observer.disconnect()
    }, [containerRef, a, b, layoutKey])

    if (!box) return null

    return (
        <span
            aria-hidden='true'
            className='pointer-events-none absolute right-2 block w-2.5 rounded-r-lg border-y-2 border-r-2 border-saffron/80 sm:right-3.5 sm:w-3.5'
            style={{ top: box.top, height: box.height }}
        >
            <span className='absolute -right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded-full bg-saffron text-primary-foreground shadow-sm sm:-right-3 sm:size-6'>
                <Equal className='size-3 sm:size-3.5' />
            </span>
        </span>
    )
}

interface TwinNoteProps {
    rows: Rows
    /** Highlights a pair when the learner points at it. */
    onPick?: (c: Case, n: Num) => void
    className?: string
}

/** "चतुर्थी = षष्ठी" with both rows of this word set side by side. Renders nothing if the rows differ. */
export const TwinNote: FC<TwinNoteProps> = ({ rows, onPick, className }): ReactNode => {
    if (!twinsMatch(rows) || !rows.chaturthi || !rows.shashthi) return null

    const left = rows.chaturthi
    const right = rows.shashthi

    return (
        <aside aria-labelledby='twin-title' className={cn('flex flex-col gap-3 rounded-xl border border-saffron/40 bg-saffron/8 px-4 py-4', className)}>
            <h3 id='twin-title' className='flex flex-wrap items-center gap-x-2 gap-y-1'>
                <span lang='hi' className='text-base font-bold'>चतुर्थी = षष्ठी</span>
                <span lang='en' className='font-sans text-sm font-medium text-muted-foreground'>Dative and genitive share their forms</span>
                <span lang='hi' className='hindi rounded-full border border-border bg-card px-2 py-0.5 text-xs font-medium'>नि.४६</span>
            </h3>
            <Bi
                hi='प्राकृत में चतुर्थी (के लिए) और षष्ठी (का, के, की) के रूप बिल्कुल एक जैसे होते हैं। कौन-सा अर्थ है, यह वाक्य से समझा जाता है।'
                en='In Prakrit, चतुर्थी (for) and षष्ठी (of) have exactly the same forms. The sentence shows which meaning is intended.'
                className='gap-0.5 text-sm leading-relaxed'
            />
            <div className='grid gap-2 sm:grid-cols-2'>
                {NUMBERS.map(n => (
                    <div key={n} className='flex flex-col gap-1.5 rounded-lg border border-border bg-card px-3 py-2.5'>
                        <span className='text-xs font-medium text-muted-foreground'>
                            <span lang='hi' className='hindi'>{NUMBER_LABEL[n].hi}</span>
                            <span aria-hidden='true'> · </span>
                            <span lang='en'>{NUMBER_LABEL[n].en}</span>
                        </span>
                        <div className='flex flex-wrap items-center gap-2'>
                            {(['chaturthi', 'shashthi'] as const).map((c, i) => (
                                <span key={c} className='flex items-center gap-2'>
                                    {i > 0 && (
                                        <>
                                            <Equal className='size-4 text-saffron-strong' aria-hidden='true' />
                                            <span className='sr-only'>बराबर · equals</span>
                                        </>
                                    )}
                                    {onPick ? (
                                        <button
                                            type='button'
                                            onClick={() => onPick(c, n)}
                                            className='flex min-h-tap flex-col items-start justify-center rounded-lg px-2 py-1 text-left hover:bg-saffron/10'
                                        >
                                            <span lang='pra' className='pra pra-sm'>{(c === 'chaturthi' ? left : right)[n].core.join(' / ')}</span>
                                            <span lang='hi' className='hindi text-xs text-muted-foreground'>{CASE_LABEL[c].hi} · {KARAK[c].hi.split(' (')[0]}</span>
                                        </button>
                                    ) : (
                                        <span className='flex flex-col px-2 py-1'>
                                            <span lang='pra' className='pra pra-sm'>{(c === 'chaturthi' ? left : right)[n].core.join(' / ')}</span>
                                            <span lang='hi' className='hindi text-xs text-muted-foreground'>{CASE_LABEL[c].hi} · {KARAK[c].hi.split(' (')[0]}</span>
                                        </span>
                                    )}
                                </span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </aside>
    )
}
